import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Mail, Lock, Globe, Clock, Camera, Save, AlertTriangle, Trash2, CheckCircle, ChevronLeft, Shield, Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, Settings as SettingsIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SelectDropdown } from '../components/SelectDropdown';
import { SettingsPanels } from '../components/SettingsPanels';
const LANGUAGE_OPTIONS = [{
  value: 'en',
  label: 'English (US)'
}, {
  value: 'es',
  label: 'Español'
}, {
  value: 'fr',
  label: 'Français'
}, {
  value: 'de',
  label: 'Deutsch'
}, {
  value: 'zh',
  label: '中文 (Chinese)'
}, {
  value: 'ja',
  label: '日本語 (Japanese)'
}];
const TIMEZONE_OPTIONS = [{
  value: 'UTC-8',
  label: 'Pacific Time (US & Canada) (UTC-08:00)'
}, {
  value: 'UTC-7',
  label: 'Mountain Time (US & Canada) (UTC-07:00)'
}, {
  value: 'UTC-6',
  label: 'Central Time (US & Canada) (UTC-06:00)'
}, {
  value: 'UTC-5',
  label: 'Eastern Time (US & Canada) (UTC-05:00)'
}, {
  value: 'UTC+0',
  label: 'Greenwich Mean Time (UTC+00:00)'
}, {
  value: 'UTC+1',
  label: 'Central European Time (UTC+01:00)'
}, {
  value: 'UTC+8',
  label: 'China Standard Time (UTC+08:00)'
}, {
  value: 'UTC+9',
  label: 'Japan Standard Time (UTC+09:00)'
}];
export function ProfilePage() {
  // Personal Info State
  const [firstName, setFirstName] = useState('Linda');
  const [lastName, setLastName] = useState('Taylor');
  const [email] = useState('linda.taylor@example.com'); // Read-only
  const [avatar, setAvatar] = useState<string | null>(null);
  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Preferences State
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC-5');
  // UI State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  // Open the Settings tab when arriving from a settings link
  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (requestedTab && ['general', 'security', 'preferences', 'settings'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const handleSave = () => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2380" data-mp="1lucssmm6t6rl">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2381" data-mp="1qnnyulj6djig">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2382" data-mp="t78hjzwi3w7d">
          <div className="flex items-center h-16" data-id="element-2383" data-mp="t6yt4gr3tx3s">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2384" data-mp="y50j7lmy6wor">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2385" data-mp="30tifssaqbub1">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2386" data-mp="2vuyosdha3sbt">
                <Home size={18} data-id="element-2387" data-mp="34uu3utdyg7h4" />
                <span className="hidden sm:inline" data-id="element-2388" data-mp="34tshhsrhnyk3">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2389" data-mp="3jiow68o1w9to">
                <BarChart3 size={18} data-id="element-2390" data-mp="3sjwj5ul49o8g" />
                <span className="hidden sm:inline" data-id="element-2391" data-mp="3sivafwrkpdtw">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2392" data-mp="3ojmf4ngvx7w8">
                <FileText size={18} data-id="element-2393" data-mp="1u4tm8w7dyv7z" />
                <span className="hidden sm:inline" data-id="element-2394" data-mp="1u5v7tum1303y">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2395" data-mp="1k1oak3r9knvt">
                <UserCog size={18} data-id="element-2396" data-mp="1ab23ixxm55oq" />
                <span className="hidden sm:inline" data-id="element-2397" data-mp="1aa0pcklaw32s">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2398" data-mp="10cm7qhnn588w">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2399" data-mp="10bb6aoho4cky">
                <Bell size={20} data-id="element-2400" data-mp="159mi34riwx5t" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2401" data-mp="rchcf5vq20b3"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2402" data-mp="wc2cxkqjigpx">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2403" data-mp="2lpz3h2vc5fav">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2404" data-mp="2qoadbdlmrpt3">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2405" data-mp="2k80homwmgta2">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2406" data-mp="2aam0um6se2h8" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2407" data-mp="37y4fuer1k0to">
                    <div className="py-2" data-id="element-2408" data-mp="330m7apgxrnm7">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2409" data-mp="331nkazhiik6k">
                        <Users size={18} className="text-gray-600" data-id="element-2410" data-mp="2t8gdrbb1cy4w" />
                        <span className="font-medium text-gray-900" data-id="element-2411" data-mp="3qolzlttwbm2w">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2412" data-mp="3lw2joq7qkeux">
                        <Home size={18} className="text-gray-600" data-id="element-2413" data-mp="22h4gax28lur" />
                        <span className="font-medium text-gray-900" data-id="element-2414" data-mp="24txbj4cwgml">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2415" data-mp="c2rk026ki5d3">
                        <UserCog size={18} className="text-gray-600" data-id="element-2416" data-mp="l1lcphgs2bld" />
                        <span className="font-medium text-gray-900" data-id="element-2417" data-mp="kzs43wmlthaq">
                          Admin Hub
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2418" data-mp="uqmtvaz26z0x"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2419" data-mp="zraqqd9yya32">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2420" data-mp="13svyjk7nby6e">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2421" data-mp="1l16a3d55rpr9"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2422" data-mp="1z0fr2f16unwa"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2423" data-mp="1u2npetaqrnew"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2424" data-mp="16cjt76zvupxx">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2425" data-mp="1bbmow0tz3jt8">
        <div data-phoebe-tour="profile" className="mb-8" data-id="element-2426" data-mp="nmbxvb2fd3sa">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2427" data-mp="nkr1dfwjvars">Profile</h1>
          <p className="text-gray-600" data-id="element-2428" data-mp="im7f1ee1nwyw">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2429" data-mp="2u913eqefhyf7">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3" data-id="element-2430" data-mp="2z2lurzlinu5f">
            <nav className="space-y-1 sticky top-24" data-id="element-2431" data-mp="2la60bn6ol4g4">
              <button onClick={() => setActiveTab('general')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'general' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2432" data-mp="2lbqv9dwwqpei">
                <User size={18} data-id="element-2433" data-mp="3hz3vh6icf4dt" />
                General
              </button>
              <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'security' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2434" data-mp="340x5ecjmy8op">
                <Shield size={18} data-id="element-2435" data-mp="1snl9cnu4aud3" />
                Security
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'preferences' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2436" data-mp="1xlmt7azdaxpm">
                <Globe size={18} data-id="element-2437" data-mp="34bmz52ag2w37" />
                Preferences
              </button>
              <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2438" data-mp="39azmdo58a56b">
                <SettingsIcon size={18} data-id="element-2439" data-mp="2ldtzq9z154ak" />
                Settings
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6" data-id="element-2440" data-mp="2qdq62fv797ta">
            {/* General Section */}
            {activeTab === 'general' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2441" data-mp="jn8p8sexao37">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2442" data-mp="tqnu1hbdzipa">
                  <div className="p-6 border-b border-gray-200" data-id="element-2443" data-mp="17hkayavyabgi">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2444" data-mp="17jwz55saxi20">
                      Personal Information
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2445" data-mp="1hfgtvov4qap6">
                      Update your photo and personal details.
                    </p>
                  </div>

                  <div className="p-6 space-y-8" data-id="element-2446" data-mp="1cj9k1kzm4f1y">
                    {/* Avatar Upload */}
                    <div className="flex items-center gap-6" data-id="element-2447" data-mp="1qekamm6tqe0i">
                      <div className="relative group" data-id="element-2448" data-mp="1mgeb9z8bf0pm">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md" data-id="element-2449" data-mp="3s7rri02353oi">
                          {avatar ? <img src={avatar} alt="Profile" className="w-full h-full object-cover" data-id="element-2450" data-mp="3o8iyixevd9u0" /> : <span className="text-2xl font-bold text-gray-500" data-id="element-2451" data-mp="1r73ltkia2amj">
                              LT
                            </span>}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-id="element-2452" data-mp="213zoh9lhw2j6">
                            <Camera className="text-white" size={24} data-id="element-2453" data-mp="260ztdh6lyz8p" />
                          </div>
                        </div>
                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2454" data-mp="2f3hfzwq6dtw8" />
                      </div>
                      <div data-id="element-2455" data-mp="2otkjiquxaq8t">
                        <h3 className="font-medium text-gray-900" data-id="element-2456" data-mp="2ottyljefjczq">
                          Profile Photo
                        </h3>
                        <p className="text-sm text-gray-500 mb-3" data-id="element-2457" data-mp="2tu89rabbv31z">
                          This will be displayed on your profile.
                        </p>
                        <div className="flex gap-3" data-id="element-2458" data-mp="m7exhcakl0pk">
                          <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] relative" data-id="element-2459" data-mp="m4spjbjroj6t">
                            Change
                            <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2460" data-mp="d5fdqc867iix" />
                          </button>
                          <button onClick={() => setAvatar(null)} className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-2461" data-mp="15v5s8mdpsmfk">
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2462" data-mp="r69lcsne9fgj">
                      <div data-id="element-2463" data-mp="r45kkjkskwkh">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2464" data-mp="1olvy8jyl0pcn">
                          First Name
                        </label>
                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-2465" data-mp="1jskmc4ewxidv" />
                      </div>
                      <div data-id="element-2466" data-mp="ugazkx81e65z">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2467" data-mp="zdjdglm5jo1f">
                          Last Name
                        </label>
                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-2468" data-mp="1n3nj0yiwhxv2" />
                      </div>
                    </div>

                    <div data-id="element-2469" data-mp="1iac7wl722uxc">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2470" data-mp="2629m2ixz9z1m">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-2471" data-mp="2auawdzbl5fp0">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2472" data-mp="3vilmcqxc9ec5" />
                        <input type="email" value={email} readOnly className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed" data-id="element-2473" data-mp="3qktvm08fjy9j" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2474" data-mp="45ikuqi1ahbq">
                        Contact support to change your email address.
                      </p>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2475" data-mp="37mhkwa1vegbu">
                    <button onClick={handleSave} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-2476" data-mp="3llqpsdkmlo0i">
                      {saveStatus === 'saving' ? <>Saving...</> : saveStatus === 'success' ? <>
                          <CheckCircle size={18} data-id="element-2477" data-mp="vxzvrqpman1m" />
                          Saved
                        </> : <>
                          <Save size={18} data-id="element-2478" data-mp="3umg5ks3m6xr0" />
                          Save Changes
                        </>}
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Security Section */}
            {activeTab === 'security' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2479" data-mp="7fw51rdxnrhe">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2480" data-mp="3fxa6d44s17j4">
                  <div className="p-6 border-b border-gray-200" data-id="element-2481" data-mp="3kp1xbfruhf9s">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2482" data-mp="3kqd6jv427f8a">
                      Password & Security
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2483" data-mp="2s7fxexnkw345">
                      Manage your password and account security.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2484" data-mp="2fpvcmp94plur">
                    <div data-id="element-2485" data-mp="2fq54yjhnc53m">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2486" data-mp="2fnsoybvg84t7">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-2487" data-mp="26nvxy3ll6tuz">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2488" data-mp="21puc5aws0gft" />
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Enter current password" data-id="element-2489" data-mp="1wzdz3rwvawtz" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2490" data-mp="1j15uqyxi4t8s">
                      <div data-id="element-2491" data-mp="1e0ru41y8clks">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2492" data-mp="1e2mad2yk3pij">
                          New Password
                        </label>
                        <div className="relative" data-id="element-2493" data-mp="36upstn2tuoz7">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2494" data-mp="2swjgrx0yftt3" />
                          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Min. 8 characters" data-id="element-2495" data-mp="2wxbq0q9ttypd" />
                        </div>
                      </div>
                      <div data-id="element-2496" data-mp="296fsdciow9a7">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2497" data-mp="2e6u2d03uptav">
                          Confirm New Password
                        </label>
                        <div className="relative" data-id="element-2498" data-mp="209yfo4uog3cc">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2499" data-mp="255nkonpgucg1" />
                          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Re-enter new password" data-id="element-2500" data-mp="1llf6xlyinmhp" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-2501" data-mp="24hwursa8v28o">
                      <h4 className="text-sm font-medium text-blue-900 mb-2" data-id="element-2502" data-mp="1zjv8yzlfooti">
                        Password Requirements
                      </h4>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside" data-id="element-2503" data-mp="29apz4f1sw8k8">
                        <li data-id="element-2504" data-mp="2na8x4f0we3it">Minimum 8 characters long</li>
                        <li data-id="element-2505" data-mp="2nasbxmv94bsm">At least one uppercase letter</li>
                        <li data-id="element-2506" data-mp="2ibo2qw8bue2a">At least one number</li>
                        <li data-id="element-2507" data-mp="2i930w7813y4j">At least one special character</li>
                      </ul>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2508" data-mp="2qsu0t4y03w1i">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2509" data-mp="34slltpmxqcp0">
                      Update Password
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden" data-id="element-2510" data-mp="275wd6wxq91u6">
                  <div className="p-6 border-b border-red-100 bg-red-50" data-id="element-2511" data-mp="2lusgyhsr3soz">
                    <h2 className="text-lg font-bold text-red-900 flex items-center gap-2" data-id="element-2512" data-mp="2h1g75dsqdqga">
                      <AlertTriangle size={20} data-id="element-2513" data-mp="1tc50jfop91u8" />
                      Danger Zone
                    </h2>
                    <p className="text-sm text-red-700 mt-1" data-id="element-2514" data-mp="1ocsh74x1ecwe">
                      Irreversible actions for your account.
                    </p>
                  </div>

                  <div className="p-6 flex items-center justify-between flex-wrap gap-4" data-id="element-2515" data-mp="8m8250jvyedo">
                    <div data-id="element-2516" data-mp="3r0hc3p0pvb7e">
                      <h3 className="font-medium text-gray-900" data-id="element-2517" data-mp="3r1a9hzdscocv">
                        Delete Account
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-2518" data-mp="3v2lyq3sqz7kj">
                        Permanently delete your account and all of your content.
                      </p>
                    </div>
                    <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium" data-id="element-2519" data-mp="rk2ffqs1rc1v">
                      Delete Account
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Preferences Section */}
            {activeTab === 'preferences' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2520" data-mp="33boapd1bsb5e">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-id="element-2521" data-mp="13xobs418n47f">
                  <div className="p-6 border-b border-gray-200" data-id="element-2522" data-mp="z322q7impnqi">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2523" data-mp="v28dnatga2w1">
                      Preferences
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2524" data-mp="q5hwsl9tn39k">
                      Customize your platform experience.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2525" data-mp="g7262jephw0l">
                    <div data-id="element-2526" data-mp="g7u0hqwkhnpa">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2527" data-mp="c4xd7mw313f0">
                        Language
                      </label>
                      <SelectDropdown fullWidth ariaLabel="Language" value={language} onChange={setLanguage} options={LANGUAGE_OPTIONS} icon={<Globe size={18} data-id="element-2529" data-mp="26glwu2d9du5q" />} data-id="element-2528" data-mp="2aj8sked1vz87" />
                    </div>

                    <div data-id="element-2530" data-mp="3ofjv8bcvlxxt">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2531" data-mp="3oe8rucnces78">
                        Time Zone
                      </label>
                      <SelectDropdown fullWidth scrollMenu ariaLabel="Time zone" value={timezone} onChange={setTimezone} options={TIMEZONE_OPTIONS} icon={<Clock size={18} data-id="element-2533" data-mp="61al9mw1p143" />} data-id="element-2532" data-mp="21jqz97b82ut" />
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2534" data-mp="35j1wvb3iuzsl">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2535" data-mp="3agk8xacu5v4t">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Settings Section */}
            {activeTab === 'settings' && <SettingsPanels data-id="element-2536" data-mp="yp6kay937541" />}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <AnimatePresence data-id="element-2537" data-mp="x9ub4wc4fosb">
        {showDeleteModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteModal(false)} data-id="element-2538" data-mp="sb0xn3wu3xs5">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2539" data-mp="16blg1yyu7rl4">
              <div className="flex items-center gap-3 mb-4" data-id="element-2540" data-mp="1q0cg62weo08d">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-2541" data-mp="1l0hlrpdf8sik">
                  <Trash2 size={24} className="text-red-600" data-id="element-2542" data-mp="1yzpq0wofyqtm" />
                </div>
                <div data-id="element-2543" data-mp="1u0mklb4juil4">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2544" data-mp="1u1fs4ebc1c4b">
                    Delete Account?
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-2545" data-mp="2djtt4p15zg9n">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6" data-id="element-2546" data-mp="24kf3tswqhbqq">
                <p className="text-sm text-red-800" data-id="element-2547" data-mp="28lilmkycdhul">
                  Are you sure you want to delete your account? All of your
                  data, including course progress and certificates, will be
                  permanently removed.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-2548" data-mp="1kxquwcc2129s">
                <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2549" data-mp="1kysithe65j8x">
                  Cancel
                </button>
                <button onClick={() => {
              // Handle delete logic
              setShowDeleteModal(false);
              alert('Account deletion request sent');
            }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm" data-id="element-2550" data-mp="1gv484pu8q7jg">
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}